import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { extractApiErrorMessage } from '../../../../utils/apiError'
import { appPrompt } from '../../../../components/AppConfirmModal'
import {
  cloneRole,
  createRole,
  getDefaultPrivileges,
  getPrivilegesTree,
  getRoleDetail,
  listRoles,
  updateRole,
} from '../api/roleApi'
import {
  evaluateRoleCapability,
  computePresetPrivilegeIds,
  SUBMODULE_DEPENDENCIES,
} from '../utils/roleCapabilities'

function normalizeText(value) {
  const trimmed = String(value || '').trim()
  return trimmed.length > 0 ? trimmed : undefined
}

export function useRoleEntryScreen() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const editingRoleId = searchParams.get('id')
  const requestedReturnTo = searchParams.get('returnTo')
  const returnTo = requestedReturnTo?.startsWith('/') && !requestedReturnTo.startsWith('//')
    ? requestedReturnTo
    : '/masters/roles/list'

  const [form, setForm] = useState({
    name: '',
    description: '',
    roleCode: '',
  })
  const [selectedPrivilegeIds, setSelectedPrivilegeIds] = useState([])
  const [operationalStatus, setOperationalStatus] = useState('active')
  const [tree, setTree] = useState([])
  const [availableRoles, setAvailableRoles] = useState([])

  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [pendingApproval, setPendingApproval] = useState(null)
  const [snapshot, setSnapshot] = useState(null)

  const savingRef = useRef(false)

  // Load tree and role data
  useEffect(() => {
    let active = true

    async function loadBootstrap() {
      setIsLoading(true)
      setError('')
      try {
        const [treeData, rolesResp] = await Promise.all([
          getPrivilegesTree(),
          listRoles({ page: 1, page_size: 100 }).catch(() => ({ items: [] })),
        ])

        if (!active) return
        setTree(Array.isArray(treeData) ? treeData : [])
        setAvailableRoles(Array.isArray(rolesResp?.items) ? rolesResp.items : [])

        if (editingRoleId) {
          const detail = await getRoleDetail(editingRoleId)
          if (!active) return

          const initialForm = {
            name: detail?.name || '',
            description: detail?.description || '',
            roleCode: detail?.role_code || '',
          }
          const initialPrivileges = Array.isArray(detail?.privilege_ids) ? detail.privilege_ids.map(Number) : []
          const initialStatus = Number(detail?.is_archive) === 1 ? 'inactive' : 'active'

          setForm(initialForm)
          setSelectedPrivilegeIds(initialPrivileges)
          setOperationalStatus(initialStatus)
          setSnapshot({
            form: initialForm,
            selectedPrivilegeIds: initialPrivileges,
            operationalStatus: initialStatus,
          })
        }
      } catch (requestError) {
        if (!active) return
        setError(extractApiErrorMessage(requestError, 'Failed to load role configuration.'))
      } finally {
        if (active) setIsLoading(false)
      }
    }

    loadBootstrap()

    return () => {
      active = false
    }
  }, [editingRoleId])

  // Total count of privileges across the tree
  const allActionIds = useMemo(() => {
    const ids = []
    tree.forEach((mod) => {
      mod.submodules?.forEach((sub) => {
        sub.actions?.forEach((act) => {
          if (act?.id) ids.push(act.id)
        })
      })
    })
    return ids
  }, [tree])

  const selectedPrivilegeSet = useMemo(() => new Set(selectedPrivilegeIds.map(Number)), [selectedPrivilegeIds])

  const securityLevelPercent = useMemo(() => {
    if (!allActionIds.length) return 0
    return Math.round((selectedPrivilegeIds.length / allActionIds.length) * 100)
  }, [allActionIds.length, selectedPrivilegeIds.length])

  const roleCapability = useMemo(() => {
    return evaluateRoleCapability(selectedPrivilegeIds, tree)
  }, [selectedPrivilegeIds, tree])

  // Map of submodule parent/child relationships and their action IDs
  const submoduleDependencyMap = useMemo(() => {
    const parentMap = {}
    const childrenMap = {}
    const actToSubKey = {}
    const subViewActId = {}
    const subAllActIds = {}

    tree.forEach((mod) => {
      mod.submodules?.forEach((sub) => {
        const subKey = sub.key
        const viewAct = sub.actions?.find((a) => String(a.type || '').toLowerCase() === 'view')
        const allIds = (sub.actions || []).map((a) => Number(a.id))

        const dep = SUBMODULE_DEPENDENCIES[subKey]
        const parentKey = sub.parent_submodule_key || dep?.parentKey || null
        if (parentKey) {
          parentMap[subKey] = parentKey
          if (!childrenMap[parentKey]) childrenMap[parentKey] = new Set()
          childrenMap[parentKey].add(subKey)
        }

        if (viewAct) subViewActId[subKey] = Number(viewAct.id)
        subAllActIds[subKey] = allIds
        allIds.forEach((id) => {
          actToSubKey[id] = subKey
        })
      })
    })

    return { parentMap, childrenMap, actToSubKey, subViewActId, subAllActIds }
  }, [tree])

  // Form management
  function patchForm(key, value) {
    setForm((prev) => ({ ...prev, [key]: value }))
  }

  // ── Granular Cascading Privilege Toggles ──

  // 1. Toggle single action (with strict upward cascading to Screen Access + Parent Submodule)
  function toggleAction(actionId, checked, parentViewId) {
    const id = Number(actionId)
    if (!id) return

    setSelectedPrivilegeIds((prev) => {
      const set = new Set(prev.map(Number))
      if (checked) {
        set.add(id)
        // If enabling an action, strictly ensure parent Screen Access (View) is also enabled
        if (parentViewId) {
          set.add(Number(parentViewId))
        }
        // Upward cascade: If this action belongs to a dependent sub-feature, ensure its parent submodule's View is enabled
        const subKey = submoduleDependencyMap.actToSubKey[id]
        if (subKey && submoduleDependencyMap.parentMap[subKey]) {
          const parentKey = submoduleDependencyMap.parentMap[subKey]
          const parentViewActId = submoduleDependencyMap.subViewActId[parentKey]
          if (parentViewActId) {
            set.add(Number(parentViewActId))
          }
        }
      } else {
        set.delete(id)
        // Downward cascade: If this action is a parent submodule's View action being disabled,
        // revoke all actions in dependent sub-features (Option A hierarchy)
        const subKey = submoduleDependencyMap.actToSubKey[id]
        if (subKey && submoduleDependencyMap.childrenMap[subKey]) {
          const isParentViewAction = submoduleDependencyMap.subViewActId[subKey] === id
          if (isParentViewAction) {
            const childKeys = submoduleDependencyMap.childrenMap[subKey]
            childKeys.forEach((childKey) => {
              const childActIds = submoduleDependencyMap.subAllActIds[childKey] || []
              childActIds.forEach((cid) => set.delete(cid))
            })
          }
        }
      }
      return Array.from(set.values())
    })
  }

  // 2. Toggle entire submodule (Screen Access master switch)
  function toggleSubmodule(submodule, checked) {
    if (!submodule?.actions) return
    const subActionIds = submodule.actions.map((a) => Number(a.id))
    const viewAction = submodule.actions.find((a) => a.type === 'view')
    const subKey = submodule.key

    setSelectedPrivilegeIds((prev) => {
      const set = new Set(prev.map(Number))
      if (checked) {
        // Turn screen access on
        if (viewAction) set.add(Number(viewAction.id))
        else subActionIds.forEach((id) => set.add(id))

        // Upward cascade: If enabling child submodule, ensure parent submodule's View is enabled
        const parentKey = submoduleDependencyMap.parentMap[subKey]
        if (parentKey) {
          const parentViewActId = submoduleDependencyMap.subViewActId[parentKey]
          if (parentViewActId) {
            set.add(Number(parentViewActId))
          }
        }
      } else {
        // Strict downward cascade: Turning screen access OFF unchecks ALL child actions
        subActionIds.forEach((id) => set.delete(id))

        // Downward cascade: If disabling parent submodule, uncheck all child sub-features
        if (submoduleDependencyMap.childrenMap[subKey]) {
          submoduleDependencyMap.childrenMap[subKey].forEach((childKey) => {
            const childActIds = submoduleDependencyMap.subAllActIds[childKey] || []
            childActIds.forEach((cid) => set.delete(cid))
          })
        }
      }
      return Array.from(set.values())
    })
  }

  // 3. Toggle entire module category
  function toggleModule(moduleObj, checked) {
    if (!moduleObj?.submodules) return
    const modActionIds = moduleObj.submodules.flatMap((s) => s.actions.map((a) => Number(a.id)))

    setSelectedPrivilegeIds((prev) => {
      const set = new Set(prev.map(Number))
      if (checked) {
        modActionIds.forEach((id) => set.add(id))
      } else {
        modActionIds.forEach((id) => set.delete(id))
      }
      return Array.from(set.values())
    })
  }

  // 4. Presets: Select All
  function selectAllPrivileges() {
    setSelectedPrivilegeIds(allActionIds)
  }

  // 5. Presets: Clear All
  function clearAllPrivileges() {
    setSelectedPrivilegeIds([])
  }

  // 6. Presets: Restore Defaults
  async function selectDefaultPrivileges() {
    try {
      const defaultIds = await getDefaultPrivileges()
      if (Array.isArray(defaultIds) && defaultIds.length > 0) {
        setSelectedPrivilegeIds(defaultIds.map(Number))
      } else {
        // Fallback: collect is_default flags from tree
        const fallbackDefaults = []
        tree.forEach((mod) => {
          mod.submodules?.forEach((sub) => {
            sub.actions?.forEach((act) => {
              if (act.is_default) fallbackDefaults.push(Number(act.id))
            })
          })
        })
        setSelectedPrivilegeIds(fallbackDefaults)
      }
    } catch {
      // Non-critical fallback
    }
  }

  // 7. Presets: Select View-Only Rights
  function selectViewOnlyPrivileges() {
    setSelectedPrivilegeIds(computePresetPrivilegeIds(tree, 'VIEW_ONLY'))
  }

  // 8. Presets: Select View + Approve Rights
  function selectViewApprovePrivileges() {
    setSelectedPrivilegeIds(computePresetPrivilegeIds(tree, 'VIEW_APPROVE'))
  }

  // 9. Presets: Safety Strip Writes (Removes Create/Edit/Delete/Archive/Upload/Import)
  function stripWritePrivileges() {
    setSelectedPrivilegeIds((prev) => computePresetPrivilegeIds(tree, 'STRIP_WRITES', prev))
  }

  // 10. Clone from another existing role
  async function cloneFromRole(sourceRoleId) {
    const id = Number(sourceRoleId)
    if (!id) return
    try {
      setIsLoading(true)
      const detail = await getRoleDetail(id)
      if (detail) {
        if (detail.privilege_ids) {
          setSelectedPrivilegeIds(detail.privilege_ids.map(Number))
        }
        if (!form.description && detail.description) {
          patchForm('description', `Cloned from ${detail.name}: ${detail.description}`)
        }
      }
    } catch (err) {
      setError(extractApiErrorMessage(err, 'Failed to copy permissions from selected role.'))
    } finally {
      setIsLoading(false)
    }
  }

  // ── Save Role ──
  async function handleSave() {
    if (savingRef.current) return
    savingRef.current = true
    setError('')
    setSuccess('')

    if (!String(form.name || '').trim()) {
      setError('Role name is required.')
      savingRef.current = false
      return
    }

    if (!selectedPrivilegeIds.length) {
      setError('At least one privilege is required.')
      savingRef.current = false
      return
    }

    const payload = {
      name: normalizeText(form.name),
      description: normalizeText(form.description),
      privilege_ids: selectedPrivilegeIds.map(Number),
      is_archive: operationalStatus === 'inactive' ? 1 : 0,
    }

    setIsSaving(true)
    try {
      let result
      if (editingRoleId) {
        result = await updateRole(editingRoleId, payload)
      } else {
        result = await createRole(payload)
      }
      const message = result?.message || (editingRoleId
        ? 'Role update submitted for approval.'
        : 'Role creation submitted for approval.')
      setPendingApproval({
        requestId: result?.approval_request_id,
        entityId: result?.entity_id,
        message,
      })
      setSuccess(message)
      savingRef.current = false
      setIsSaving(false)
    } catch (requestError) {
      setError(extractApiErrorMessage(requestError, 'Failed to save role configuration.'))
      savingRef.current = false
      setIsSaving(false)
    }
  }

  // ── Clone Current Role via Backend Endpoint ──
  async function handleClone() {
    if (!editingRoleId || savingRef.current) return

    const nextName = await appPrompt({ title: 'Clone Role', message: 'Enter cloned role name:', defaultValue: `${form.name || 'Role'} Copy`, confirmText: 'Clone Role' })
    if (!nextName || !nextName.trim()) return

    setError('')
    setSuccess('')
    savingRef.current = true
    setIsSaving(true)

    try {
      const response = await cloneRole(editingRoleId, {
        name: nextName.trim(),
        description: normalizeText(form.description),
      })
      const message = response?.message || 'Role clone submitted for approval.'
      setPendingApproval({
        requestId: response?.approval_request_id,
        entityId: response?.entity_id,
        message,
      })
      setSuccess(message)
      savingRef.current = false
      setIsSaving(false)
    } catch (requestError) {
      setError(extractApiErrorMessage(requestError, 'Failed to clone role.'))
      savingRef.current = false
      setIsSaving(false)
    }
  }

  function handleCancelChanges() {
    if (!snapshot) {
      navigate(returnTo)
      return
    }
    setForm(snapshot.form)
    setSelectedPrivilegeIds(snapshot.selectedPrivilegeIds)
    setOperationalStatus(snapshot.operationalStatus)
    setSuccess('')
    setError('')
  }

  return {
    state: {
      form,
      operationalStatus,
      tree,
      availableRoles: availableRoles.filter((r) => String(r.id) !== String(editingRoleId)),
      selectedPrivilegeIds,
      isLoading,
      isSaving,
      error,
      success,
      pendingApproval,
      returnTo,
      isEditMode: Boolean(editingRoleId),
      securityLevelPercent,
      roleCapability,
      selectedPrivilegeCount: selectedPrivilegeIds.length,
      totalPrivilegeCount: allActionIds.length,
    },
    actions: {
      patchForm,
      setOperationalStatus,
      toggleAction,
      toggleSubmodule,
      toggleModule,
      selectAllPrivileges,
      clearAllPrivileges,
      selectDefaultPrivileges,
      selectViewOnlyPrivileges,
      selectViewApprovePrivileges,
      stripWritePrivileges,
      cloneFromRole,
      handleSave,
      handleClone,
      handleCancelChanges,
    },
  }
}
